import {CommonModule} from '@angular/common';
import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ToastService, TranslationService} from '@emma-labs/shared/angular';
import {ErrorResponseCode, SaleReportStatus} from '@emma-labs/shared/typescript';
import {ToastModule} from 'primeng/toast';
import {first, Observable} from 'rxjs';
import {ArchivedProposalPageObject} from '../../entities/archived-proposal-object/archived-proposal-page-object';
import {RevisionAnswers} from '../../entities/archived-proposal-object/revision-answers';
import {Client} from '../../entities/proposal-page-object/client';
import {ArchivedProposalPageOrchestrator} from '../../services/proposal/archived-proposal-page.orchestrator';
import {MainComponent} from '../main/main.component';
import {SkeletonComponent} from '../main/skeleton/skeleton.component';
import {AddressSectionComponent} from '../proposal-page/address-section/address-section.component';
import {BeneficiariesSectionComponent} from '../proposal-page/beneficiaries-section/beneficiaries-section.component';
import {ClientSectionComponent} from '../proposal-page/client-section/client-section.component';
import {DisclosuresSectionComponent} from '../proposal-page/disclosures-section/disclosures-section.component';
import {FnaSectionComponent} from '../proposal-page/fna-section/fna-section.component';
import {HealthQuestionnaireSectionComponent} from '../proposal-page/health-questionnaire-section/health-questionnaire-section.component';
import {IdDocumentSectionComponent} from '../proposal-page/id-document-section/id-document-section.component';
import {SaleReportsSectionComponent} from '../sale-report-page/sale-reports-section/sale-reports-section.component';
import {ArchivedProtectionSectionComponent} from './archived-protection-section/archived-protection-section.component';
import {CoverageModifierSectionComponent} from './coverage-modifier-section/coverage-modifier-section.component';
import {ExtraQuestionnairesSectionComponent} from './extra-questionnaires-section/extra-questionnaires-section.component';
import {ModificationNoticeSectionComponent} from './modification-notice-section/modification-notice-section.component';
import {RefusalNoticeSectionComponent} from './refusal-notice-section/refusal-notice-section.component';

@Component({
  selector: 'em-archived-proposal-page',
  templateUrl: './archived-proposal-page.component.html',
  imports: [
    CommonModule,
    MainComponent,
    ClientSectionComponent,
    FnaSectionComponent,
    HealthQuestionnaireSectionComponent,
    ArchivedProtectionSectionComponent,
    BeneficiariesSectionComponent,
    AddressSectionComponent,
    IdDocumentSectionComponent,
    DisclosuresSectionComponent,
    ToastModule,
    SaleReportsSectionComponent,
    ExtraQuestionnairesSectionComponent,
    SkeletonComponent,
    ModificationNoticeSectionComponent,
    CoverageModifierSectionComponent,
    RefusalNoticeSectionComponent,
  ],
})
export class ArchivedProposalPageComponent implements OnDestroy {
  archivedProposalPageObject$: Observable<ArchivedProposalPageObject>;
  readonly saleReportStatus = SaleReportStatus;

  constructor(
    readonly orchestrator: ArchivedProposalPageOrchestrator,
    private readonly translate: TranslationService,
    private readonly toastService: ToastService,
    route: ActivatedRoute,
  ) {
    let archivedProposalId: string | undefined;

    if (route.snapshot.params['archivedProposalId']) {
      archivedProposalId = atob(route.snapshot.params['archivedProposalId']);
    }

    if (!archivedProposalId) {
      throw new Error('No archived proposal id found');
    }

    this.archivedProposalPageObject$ = orchestrator.get(archivedProposalId);
  }

  clientFullName(client: Client): string {
    return `${client.firstName} ${client.lastName}`;
  }

  updateExtraQuestionnaires(event: {output: RevisionAnswers[]; callback?: (status: boolean) => void}) {
    this.orchestrator
      .answerRevisions(event.output)
      .pipe(first())
      .subscribe({
        next: () => event.callback?.(true),
        error: () => {
          const errorMessage = this.translate.currentLanguageIsFr()
            ? "L'enregistrement des réponses aux questionnaires supplémentaires a échoué."
            : "Failed to save additional questionnaires' answers.";
          event.callback?.(false);
          this.toastService.error(errorMessage);
        },
      });
  }

  completeExtraQuestionnaires(event: {output: string[]; callback?: (status: boolean) => void}) {
    this.orchestrator
      .completeExtraQuestionnaires(event.output)
      .pipe(first())
      .subscribe({
        next: () => event.callback?.(true),
        error: () => {
          const errorMessage = this.translate.currentLanguageIsFr()
            ? 'La complétion des questionnaires supplémentaires a échoué.'
            : 'Failed to complete the additional questionnaires.';
          event.callback?.(false);
          this.toastService.error(errorMessage);
        },
      });
  }

  uploadDocuments(event: {
    healthAnswersRevisionId: string;
    documents: File[];
    callback: (status: boolean) => void;
  }) {
    this.orchestrator
      .uploadHealthAnswersRevisionDocuments(event.healthAnswersRevisionId, event.documents)
      .pipe(first())
      .subscribe({
        next: () => event.callback?.(true),
        error: () => {
          const errorMessage = this.translate.currentLanguageIsFr()
            ? 'Le téléversement des documents a échoué.'
            : 'Failed to upload documents.';
          event.callback?.(false);
          this.toastService.error(errorMessage);
        },
      });
  }

  removeDocument(event: {
    healthAnswersRevisionId: string;
    documentName: string;
    callback: (status: boolean) => void;
  }) {
    this.orchestrator
      .removeHealthAnswersRevisionDocument(event.healthAnswersRevisionId, event.documentName)
      .pipe(first())
      .subscribe({
        next: () => event.callback?.(true),
        error: () => {
          const errorMessage = this.translate.currentLanguageIsFr()
            ? 'La suppression du document a échoué.'
            : 'Failed to remove the document.';
          event.callback?.(false);
          this.toastService.error(errorMessage);
        },
      });
  }

  downloadDocument($event: {id: string; documentName: string}) {
    this.orchestrator
      .downloadDocument($event.id, $event.documentName)
      .pipe(first())
      .subscribe({
        next: (blob) => {
          const file = new Blob([blob], {type: 'application/pdf'});
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL, '_blank');
        },
        error: () => {
          const errorMessage = this.translate.currentLanguageIsFr()
            ? 'Erreur lors du téléchargement du document.'
            : 'Error downloading document.';
          this.toastService.error(errorMessage);
        },
      });
  }

  onSendModificationNotice(callback: () => void): void {
    this.orchestrator
      .sendModificationNotice()
      .pipe(first())
      .subscribe({
        next: () => {
          const successMessage = this.translate.currentLanguageIsFr()
            ? "L'avis de modification a été envoyé avec succès."
            : 'The modification notice has been sent successfully.';
          this.toastService.success(successMessage);
          callback();
        },
        error: (e) => {
          let errorMessage: string;
          if ((e.code = ErrorResponseCode.DECISION_ALREADY_MADE)) {
            errorMessage = this.translate.currentLanguageIsFr()
              ? 'Une décision a déjà été prise par le client.'
              : 'A decision has already been made by the client.';
          } else {
            errorMessage = this.translate.currentLanguageIsFr()
              ? "L'envoi de l'avis de modification a échoué."
              : 'Failed to send modification notice.';
          }
          this.toastService.error(errorMessage);
          callback();
        },
      });
  }

  ngOnDestroy(): void {
    this.orchestrator.clear();
  }
}
